/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

(() => {
  'use strict';

  const sel = (k) => document.querySelector(`[data-cf-column-key="${k}"]`);

  function zipcloud(zip) {
    zip = String(zip || '').replace(/[^\d]/g, '');
    if (zip.length !== 7) return Promise.resolve(null);

    return new Promise((resolve) => {
      const cb = `__zipcb_${Date.now()}_${Math.random().toString(16).slice(2)}`;
      const s  = document.createElement('script');

      window[cb] = (data) => {
        try { resolve(data?.results?.[0] ?? null); }
        finally { delete window[cb]; s.remove(); }
      };

      s.src = `https://zipcloud.ibsnet.co.jp/api/search?zipcode=${encodeURIComponent(zip)}&callback=${cb}`;
      document.head.appendChild(s);
    });
  }

  const PREF_TO_VALUE = {
    '北海道':'Hokkaido','青森県':'Aomori','岩手県':'Iwate','宮城県':'Miyagi','秋田県':'Akita','山形県':'Yamagata','福島県':'Fukushima',
    '茨城県':'Ibaraki','栃木県':'Tochigi','群馬県':'Gunma','埼玉県':'Saitama','千葉県':'Chiba','東京都':'Tokyo','神奈川県':'Kanagawa',
    '新潟県':'Niigata','富山県':'Toyama','石川県':'Ishikawa','福井県':'Fukui','山梨県':'Yamanashi','長野県':'Nagano',
    '岐阜県':'Gifu','静岡県':'Shizuoka','愛知県':'Aichi','三重県':'Mie',
    '滋賀県':'Shiga','京都府':'Kyoto','大阪府':'Osaka','兵庫県':'Hyogo','奈良県':'Nara','和歌山県':'Wakayama',
    '鳥取県':'Tottori','島根県':'Shimane','岡山県':'Okayama','広島県':'Hiroshima','山口県':'Yamaguchi',
    '徳島県':'Tokushima','香川県':'Kagawa','愛媛県':'Ehime','高知県':'Kochi',
    '福岡県':'Fukuoka','佐賀県':'Saga','長崎県':'Nagasaki','熊本県':'Kumamoto','大分県':'Oita','宮崎県':'Miyazaki','鹿児島県':'Kagoshima',
    '沖縄県':'Okinawa'
  };

  function fire(el){
    if (!el) return;
    el.dispatchEvent(new Event('input',  { bubbles:true }));
    el.dispatchEvent(new Event('change', { bubbles:true }));
  }

  function setNativeValue(el, value) {
    if (!el) return;
    const proto = el.tagName === 'SELECT'
      ? HTMLSelectElement.prototype
      : HTMLInputElement.prototype;

    const desc = Object.getOwnPropertyDescriptor(proto, 'value');
    if (desc && typeof desc.set === 'function') {
      desc.set.call(el, value ?? '');
    } else {
      el.value = value ?? '';
    }
  }

  function setSelectByPref(selectEl, prefJp){
    if (!selectEl || selectEl.tagName !== 'SELECT') return;

    const targetValue = PREF_TO_VALUE[prefJp] || '';
    if (!targetValue) return;

    setNativeValue(selectEl, targetValue);

    if (selectEl.value !== targetValue) {
      const opts = Array.from(selectEl.options || []);
      const hit = opts.find(o => (o.value || '') === targetValue)
               || opts.find(o => (o.textContent || '').trim() === prefJp);
      if (hit) setNativeValue(selectEl, hit.value);
    }

    fire(selectEl);
  }

  function setTextStable(inputEl, v){
    if (!inputEl) return;
    const value = v ?? '';

    const apply = () => {
      setNativeValue(inputEl, value);
      fire(inputEl);
    };

    apply();
    requestAnimationFrame(apply);
    setTimeout(apply, 120);
    setTimeout(apply, 350);
  }

  // ✅ ロック解除（編集できるようにする）
  function unlockFields(provinceEl, cityEl){
    if (provinceEl && provinceEl.tagName === 'SELECT') {
      provinceEl.style.pointerEvents = '';
      provinceEl.style.backgroundColor = '';
    }
    if (cityEl && cityEl.tagName !== 'SELECT') {
      cityEl.readOnly = false;
      cityEl.style.backgroundColor = '';
    }
  }

  function init(){
    const zipEl = sel('default_address.zip');
    if (!zipEl) return false;

    const provinceEl = sel('default_address.province');
    const cityEl     = sel('default_address.city');
    const address1El = sel('default_address.address1');

    let lastZip = '';
    let lastCity = '';
    let lastTown = '';

    // ✅ まず解除しておく（常に編集OK）
    unlockFields(provinceEl, cityEl);

    const guardCity = () => {
      // ※「消えたら戻す」挙動は残す（郵便番号自動入力の安定性のため）
      if (!cityEl) return;
      if (lastCity && (cityEl.value || '').trim() === '') {
        setTextStable(cityEl, lastCity);
      }
    };

    if (cityEl) {
      cityEl.addEventListener('blur', guardCity, true);
      cityEl.addEventListener('change', guardCity, true);
    }

    const run = async () => {
      const zip = (zipEl.value || '').replace(/[^\d]/g,'');
      if (zip.length !== 7 || zip === lastZip) return;
      lastZip = zip;

      const r = await zipcloud(zip);
      if (!r) return;

      setSelectByPref(provinceEl, r.address1);

      lastCity = r.address2 || '';
      lastTown = r.address3 || '';

      setTimeout(() => {
        setTextStable(cityEl, lastCity);
        setTextStable(address1El, lastTown);

        // ✅ ロックしない（ここでapplyReadonlyしてたのを削除）
        // unlockFields(provinceEl, cityEl); // 念のため呼ぶならこれ

        setTimeout(guardCity, 200);
      }, 80);
    };

    zipEl.addEventListener('input', run, { passive:true });
    zipEl.addEventListener('blur',  run, { passive:true });

    return true;
  }

  if (!init()) {
    const mo = new MutationObserver(() => { if (init()) mo.disconnect(); });
    mo.observe(document.documentElement, { childList:true, subtree:true });
  }
})();
